import { cloneElement, isValidElement, useEffect, useId, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Chip } from '@cowoo/shared/ui-web';

/**
 * The shapes every backoffice list screen is made of (10-web-admin § 3).
 *
 * § 3 describes one layout for all of them — a card with a header and filter
 * chips, a table whose rows open a 440 px detail panel, `Esc` to close, the
 * filters and the open panel in the URL. Building that once means a new screen
 * is a table and a panel body, and that the eleven screens cannot drift apart
 * a pixel at a time.
 */

export function Page({ children }: { children: ReactNode }) {
  return <main className="flex gap-5 px-6 py-5">{children}</main>;
}

export function ListCard({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-line rounded-card flex min-w-0 flex-1 flex-col gap-3.5 border bg-white px-5 py-[18px]">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-webH3 text-ink">{title}</h1>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export interface FilterOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

/** The chip row of § 3: every chip shows its count, including unselected ones. */
export function FilterChips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: FilterOption<T>[];
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <Chip
          key={option.value}
          size="md"
          selected={option.value === value}
          onPress={() => onChange(option.value)}
          label={option.count === undefined ? option.label : `${option.label} · ${option.count}`}
          testID={`filter-${option.value}`}
        />
      ))}
    </div>
  );
}

export function Table({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="text-adminBody w-full border-collapse text-left">
        <thead className="sticky top-0 bg-white">
          <tr>{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Th({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return (
    <th
      scope="col"
      className={`border-line text-muted font-body border-b pb-2 pr-2 text-[11px] font-bold uppercase tracking-[0.06em] ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </th>
  );
}

export function Td({ children, bold, numeric }: { children?: ReactNode; bold?: boolean; numeric?: boolean }) {
  return (
    <td
      className={`border-hairline border-b py-2.5 pr-2 align-middle ${bold ? 'font-semibold' : ''} ${
        numeric ? 'text-right tabular-nums' : ''
      }`}
    >
      {children}
    </td>
  );
}

/** A row that opens the detail panel; § 3 puts the open panel in the URL. */
export function Row({ to, selected, children }: { to: string; selected: boolean; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <tr
      onClick={() => navigate(to)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(to);
        }
      }}
      tabIndex={0}
      aria-selected={selected}
      data-testid="list-row"
      className={`hover:bg-fog focus-visible:ring-teal-dark cursor-pointer focus-visible:outline-none focus-visible:ring-2 ${
        selected ? 'bg-teal-tint' : ''
      }`}
    >
      {children}
    </tr>
  );
}

/** The 440 px detail panel of § 3. `Esc` closes it by leaving the child route. */
export function Panel({
  label,
  title,
  subtitle,
  onClose,
  children,
}: {
  label: string;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      aria-label={label}
      className="border-line rounded-card flex w-[440px] flex-none flex-col gap-3 overflow-y-auto border bg-white px-5 py-[18px]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-label text-muted uppercase">{label}</p>
          <p className="text-title text-ink pt-1">{title}</p>
          {subtitle ? <p className="text-adminBody text-muted">{subtitle}</p> : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="hover:bg-fog text-muted rounded-buttonSm -mr-1 -mt-1 flex h-8 w-8 items-center justify-center"
        >
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      {children}
    </aside>
  );
}

/** A label/value line inside a panel, as every artboard draws them. */
export function PanelRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="border-hairline text-adminBody flex justify-between gap-3 border-b py-1.5">
      <span className="text-muted">{label}</span>
      <span className="text-ink text-right font-semibold">{value}</span>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="text-adminBody text-muted py-10 text-center">{children}</p>;
}

/**
 * The line icons the artboards draw: 24-unit viewBox, 2 px round stroke,
 * `currentColor`. The paths are the boards' own, copied rather than replaced
 * by a library's look-alikes — a rail whose icons differ from the design is
 * the difference the owner notices first.
 *
 * Every icon is `aria-hidden`; the control it sits in carries the name.
 */
const ICON_PATHS = {
  back: ['m15 18-6-6 6-6'],
  select: ['M5 12h14', 'm12 5 7 7-7 7'],
  desk: ['M3 10h18', 'M5 10v9', 'M19 10v9', 'M9 10V6h6v4', 'M5 15h14'],
  room: [
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2',
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    'M23 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  locker: [
    'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z',
    'M7 11V7a5 5 0 0 1 10 0v4',
  ],
  parking: ['M3 12l2-5h14l2 5v6H3z', 'M3 12h18', 'M7 18v2', 'M17 18v2', 'M7 15h.01', 'M17 15h.01'],
  /** A door in a frame — the one tool the board's rail leaves out (§ 6.3 has it). */
  office: ['M4 21V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16', 'M2 21h20', 'M15 12h.01'],
  zone: ['M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z', 'M3 9h18', 'M9 21V9'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z'],
  plus: ['M12 5v14', 'M5 12h14'],
  minus: ['M5 12h14'],
  chevronDown: ['m6 9 6 6 6-6'],
  check: ['m20 6-11 11-5-5'],
  document: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'm9 15 2 2 4-4'],
  refund: [
    'M3 12a9 9 0 1 0 3-6.7',
    'M3 3v6h6',
    'M12 8v8',
    'M9.5 10.5h3.75a1.75 1.75 0 0 1 0 3.5h-2.5a1.75 1.75 0 0 0 0 3.5H15',
  ],
  task: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'm9 11 3 3L22 4'],
  calendar: [
    'M16 2v4',
    'M8 2v4',
    'M3 10h18',
    'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  ],
  user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  chevronLeft: ['m15 18-6-6 6-6'],
  chevronRight: ['m9 18 6-6-6-6'],
  sparkle: ['M12 3l1.9 5.6L19.5 10.5l-5.6 1.9L12 18l-1.9-5.6L4.5 10.5l5.6-1.9z'],
  close: ['M18 6 6 18', 'm6 6 12 12'],
  arrowRight: ['M5 12h14', 'm12 5 7 7-7 7'],
  search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'm21 21-4.35-4.35'],
  warn: [
    'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
    'M12 9v4',
    'M12 17h.01',
  ],
  image: ['M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z', 'M3 9h18', 'M9 21V9'],
  sticker: [
    'M3 3h6v6H3z',
    'M15 3h6v6h-6z',
    'M3 15h6v6H3z',
    'M15 15h2v2h-2z',
    'M19 15h2v2h-2z',
    'M15 19h2v2h-2z',
    'M19 19h2v2h-2z',
    'M11 3v4',
    'M11 11h2',
    'M3 11h4',
    'M17 11h4',
    'M11 17v4',
  ],
} as const;

export type IconName = keyof typeof ICON_PATHS;

export function Icon({ name, size = 18, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`block flex-none ${className ?? ''}`}
    >
      {ICON_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/**
 * A key on the keyboard, for the shortcut hints — `<kbd>` so a screen reader
 * says it is one, drawn as the small bordered cap the boards use for
 * "Cmd+D" and "Entf".
 */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="border-line text-ink font-body inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border bg-white px-1 text-[11px] font-semibold not-italic shadow-[0_1px_0_#dfe6e8]">
      {children}
    </kbd>
  );
}

/**
 * The modal of `AdminDialogs` — `.dlg` there: white, 16 px radius, 10 px gap,
 * actions bottom right.
 *
 * The artboard draws the dialogs flat side by side to show them all at once;
 * on screen the same card sits on a scrim, which is what the shadow token
 * `lg` is for.
 *
 * `Esc` and a click outside both cancel. Submitting goes through a form, so
 * Enter works and the primary button is a real submit — a dialog whose only
 * way out is the mouse is one staff will fight every day.
 */
export function Dialog({
  title,
  intro,
  tone,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
  disabled,
  aside,
  wide,
  cancelLabel,
  children,
}: {
  title: string;
  intro?: ReactNode;
  tone?: 'danger';
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  busy?: boolean;
  disabled?: boolean;
  /**
   * A second column beside the fields, for a dialog that previews what it is
   * about to make — § 8 draws the first sticker sheet next to its options.
   *
   * A prop rather than a second modal so the frame stays one thing: the same
   * border, radius, padding and button row, widened. Two modals drift.
   */
  aside?: ReactNode;
  /** The `aside` width for a single column that needs room, e.g. the device log (§ 15). */
  wide?: boolean;
  /** "Schließen" where the dialog only shows something and there is nothing to cancel. */
  cancelLabel?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* A real button rather than a div with a click handler: the scrim is a
          way out of the dialog, so it has to be reachable without a mouse. */}
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[rgba(21,21,21,0.35)]"
      />
      <form
        role="dialog"
        aria-label={title}
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm();
        }}
        className={`border-line rounded-card relative flex max-w-full flex-col gap-2.5 border bg-white px-[18px] py-4 shadow-lg ${
          aside || wide ? 'w-[840px]' : 'w-[420px]'
        }`}
      >
        <p className={`text-title ${tone === 'danger' ? 'text-danger-text' : 'text-ink'}`}>{title}</p>
        {intro ? <div className="text-adminSmall text-muted">{intro}</div> : null}
        {aside ? (
          <div className="flex gap-5">
            <div className="flex w-[380px] shrink-0 flex-col gap-2.5">{children}</div>
            <div className="min-w-0 flex-1">{aside}</div>
          </div>
        ) : (
          children
        )}
        <div className="flex justify-end gap-2 pt-1.5">
          <Button size="sm" variant="secondary" type="button" onClick={onClose}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            size="sm"
            variant={tone === 'danger' ? 'danger' : 'primary'}
            type="submit"
            loading={busy}
            disabled={disabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * A labelled field — `.ff` + `.lab` + `.fld` of the artboard.
 *
 * The label points at the control by id rather than wrapping it. Wrapping made
 * the control's accessible name the label *and* the hint run together
 * ("Enthaltene PlätzeHöchstens so viele wie Plätze im Büro"), which is what a
 * screen reader then announces, and what `getByLabel` then has to match.
 */
export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-label text-muted uppercase">
        {label}
      </label>
      {isValidElement<{ id?: string }>(children) ? cloneElement(children, { id }) : children}
      {hint ? <p className="text-adminSmall text-muted leading-normal">{hint}</p> : null}
    </div>
  );
}

/** The 40 px dialog control of `.fld`; the 48 px `h-field` is the page form. */
export const fieldClass =
  'border-line-strong rounded-input text-adminBody h-10 w-full border bg-white px-3 disabled:bg-fog';

/**
 * The 40 × 24 switch the artboards draw for a yes/no that takes effect at
 * once — "Schnittmarken", "Einrasten", "Belegung heute". A checkbox is the
 * same fact drawn as a form field; the boards draw a switch, so this is one.
 * The knob slides by justification, which is how the board draws it too.
 */
export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`flex h-6 w-10 flex-none items-center rounded-full p-[3px] transition-colors ${
        checked ? 'bg-teal justify-end' : 'bg-placeholder justify-start'
      }`}
    >
      <span className="block h-[18px] w-[18px] rounded-full bg-white" />
    </button>
  );
}

/** A switch with its label on the left, the row the boards draw in cards. */
export function SwitchRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="text-adminBody text-ink flex items-center justify-between gap-3">
      <span>{label}</span>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

/**
 * The boards' radio card: a 40 px row with a 2 px border that turns teal when
 * chosen, a round mark on the left and, optionally, a count on the right.
 * Used for "Welche Plätze" and the desk's status.
 */
export function RadioCard({
  name,
  value,
  checked,
  disabled,
  onChange,
  mark,
  children,
  trailing,
}: {
  name: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  /** Replaces the round mark, for the status rows whose mark shows the colour. */
  mark?: ReactNode;
  children: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <label
      className={`text-adminBody rounded-input flex h-10 cursor-pointer items-center gap-2.5 border-2 px-3 font-semibold ${
        checked ? 'border-teal bg-[#f3f8f9]' : 'border-line'
      } ${disabled ? 'text-disabled cursor-not-allowed' : 'text-ink'}`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="sr-only"
      />
      {mark ?? (
        <span
          className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full ${
            checked ? 'bg-teal' : 'border-placeholder border-2'
          }`}
        >
          {checked ? <Icon name="check" size={11} className="text-white [stroke-width:3]" /> : null}
        </span>
      )}
      <span className="flex-1">{children}</span>
      {trailing !== undefined ? <span className="text-muted font-medium">{trailing}</span> : null}
    </label>
  );
}

/**
 * A segmented control — the fog track with the chosen segment raised on a
 * shadow — for a choice between two or three words: FlexDesk | PrivateDesk,
 * App | Web-App | Terminal.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  size = 'md',
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
  /** `md` is the panel's 32 px segment; `sm` the 30 px one beside a heading. */
  size?: 'sm' | 'md';
}) {
  return (
    <div role="radiogroup" aria-label={label} className="bg-fog flex gap-[3px] rounded-[10px] p-[3px]">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={`flex flex-1 items-center justify-center whitespace-nowrap rounded-[8px] px-3 text-[13px] font-semibold ${
            size === 'sm' ? 'h-[30px]' : 'h-8'
          } ${
            option.value === value ? 'text-ink bg-white shadow-[0_1px_3px_rgba(21,21,21,0.12)]' : 'text-muted'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A 40 px square with an icon and no words — the boards' tool rail, the back
 * button and the zoom buttons. The name is on the control, for tooltips and
 * screen readers; the picture is the label.
 */
/**
 * An icon-only button with its name on a tooltip (10 § 6.3: "icons get
 * tooltips + shortcuts"). The tooltip is ours, not the browser's `title`:
 * it appears at once on hover and on keyboard focus, and it reads the same
 * as the accessible name. It is drawn by the button's own `::after`, so the
 * button stays what it is to the DOM — an icon and no words.
 */
export function IconTile({
  icon,
  label,
  active,
  size = 40,
  onClick,
  testId,
  tooltip = 'right',
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  size?: 36 | 40;
  onClick: () => void;
  testId?: string;
  /** Where the tooltip sits; a rail on the left edge wants it to the right. */
  tooltip?: 'right' | 'top';
}) {
  const placement =
    tooltip === 'right'
      ? 'after:left-full after:top-1/2 after:ml-2 after:-translate-y-1/2'
      : 'after:bottom-full after:left-1/2 after:mb-2 after:-translate-x-1/2';
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      data-testid={testId}
      data-tooltip={label}
      onClick={onClick}
      className={`rounded-input after:bg-ink relative flex flex-none items-center justify-center after:pointer-events-none after:absolute after:z-20 after:whitespace-nowrap after:rounded-[8px] after:px-2.5 after:py-1.5 after:text-xs after:font-medium after:text-white after:opacity-0 after:shadow-md after:transition-opacity after:content-[attr(data-tooltip)] hover:after:opacity-100 focus-visible:after:opacity-100 ${placement} ${
        size === 36 ? 'h-9 w-9' : 'h-10 w-10'
      } ${active ? 'bg-teal text-white' : 'text-ink hover:bg-fog'}`}
    >
      <Icon name={icon} size={size === 36 ? 18 : 20} />
    </button>
  );
}

/**
 * The bar under the shell on every editor screen (10-web-admin § 6.2, § 6.3,
 * § 7): a back tile, the plan's name in Syne 18 with an optional chip, a
 * subline, and the screen's actions on the right.
 */
export function EditorBar({
  onBack,
  title,
  chip,
  subline,
  middle,
  actions,
}: {
  onBack: () => void;
  title: string;
  chip?: ReactNode;
  subline: ReactNode;
  middle?: ReactNode;
  actions: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="border-line flex flex-none items-center justify-between gap-4 border-b bg-white px-6 py-2.5">
      <div className="flex min-w-0 items-center gap-3.5">
        <span className="bg-fog rounded-input flex h-9 w-9 flex-none items-center justify-center">
          <IconTile icon="back" label={t('common.back')} size={36} onClick={onBack} />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex items-center gap-2.5">
            <p className="font-heading text-ink truncate text-lg font-bold">{title}</p>
            {chip}
          </div>
          <p className="font-body text-muted text-xs">{subline}</p>
        </div>
      </div>
      {middle ? <div className="flex items-center gap-3">{middle}</div> : null}
      <div className="flex flex-none items-center gap-2.5">{actions}</div>
    </header>
  );
}

/** The 26 px amber "Entwurf" chip of the editor bar. */
export function DraftChip({ children }: { children: ReactNode }) {
  return (
    <span className="bg-warn-bg text-warn-text font-body inline-flex h-[26px] items-center rounded-full px-2.5 text-[13px] font-semibold">
      {children}
    </span>
  );
}
